import { Img } from "@/components/site/Img";
import { useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrolledPast } from "@/hooks/useScrolledPast";
import SiteLink from "@/components/site/SiteLink";

const NAV_SCROLL_THRESHOLD_PX = 40;

const links = [
  { label: "Fountain Services", href: "/fountain-repair" },
  { label: "Available Pieces", href: "/available-now" },
  { label: "Custom Builds", href: "/builds" },
  { label: "The Majestic", href: "/majestic" },
  { label: "About", href: "/#about" },
  { label: "Contact", href: "/#contact" },
];

const NavLogo = ({ scrolled }) => (
  <Link
    data-testid="nav-logo"
    to="/"
    aria-label="Garden Fountains Yard Sculptures — Home"
    className={`flex items-center gap-3 font-serif leading-tight flex-shrink-0 ${
      scrolled ? "text-[#2C2A28]" : "text-white"
    }`}
  >
    <Img
      src="/images/gfys-logo.jpg"
      alt="Garden Fountains Yard Sculptures logo"
      data-testid="nav-logo-image"
      sizes="56px"
      className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-[#C8B387]/70 ring-2 ring-[#F9F6F0]/30 shadow-[0_2px_12px_rgba(0,0,0,0.35)] bg-[#F9F6F0] flex-shrink-0"
    />
    <div>
      <div className="text-xl md:text-2xl tracking-tight font-medium whitespace-nowrap">
        Garden Fountains
        <span className="font-light"> Yard Sculptures</span>
      </div>
      <div
        className={`text-[10px] md:text-[11px] tracking-[0.28em] uppercase mt-1 ${
          scrolled ? "text-[#7A6547]" : "text-[#E8DFC9]"
        }`}
      >
        Villa Rica, Georgia
      </div>
    </div>
  </Link>
);

const MobileMenu = ({ onNavigate }) => (
  <div
    id="nav-mobile-menu"
    data-testid="nav-mobile-menu"
    onKeyDown={(e) => e.key === "Escape" && onNavigate()}
    className="xl:hidden bg-[#F9F6F0] border-t border-[#D5CEBD] px-6 py-6"
  >
    <div className="flex flex-col gap-4 text-[#2C2A28]">
      {links.map((l) => (
        <SiteLink
          key={l.href}
          href={l.href}
          onClick={onNavigate}
          data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className="nav-link text-base"
        >
          {l.label}
        </SiteLink>
      ))}
      <a
        href="tel:+16787699652"
        data-testid="nav-mobile-phone"
        className="inline-flex items-center gap-2 mt-2 text-sm text-[#7A6547] tracking-[0.18em] uppercase"
      >
        <Phone size={14} strokeWidth={1.5} /> (678) 769-9652
      </a>
    </div>
  </div>
);

export default function Navbar() {
  const scrolled = useScrolledPast(NAV_SCROLL_THRESHOLD_PX);
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F6F0] border-b border-[#D5CEBD] shadow-sm"
          : "bg-[#1F1E1C] border-b border-white/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-5 md:py-6 flex items-center justify-between">
        <NavLogo scrolled={scrolled} />

        <nav
          className={`hidden xl:flex items-center gap-4 2xl:gap-7 text-[13px] 2xl:text-sm whitespace-nowrap ml-6 ${
            scrolled ? "text-[#2C2A28]" : "text-white"
          }`}
        >
          {links.map((l) => (
            <SiteLink
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="nav-link"
            >
              {l.label}
            </SiteLink>
          ))}
        </nav>

        <a
          href="tel:+16787699652"
          data-testid="nav-phone-cta"
          className={`hidden md:inline-flex items-center gap-2 px-4 xl:px-5 py-2.5 text-xs tracking-[0.18em] uppercase border transition-colors whitespace-nowrap ml-4 ${
            scrolled
              ? "border-[#2C2A28] text-[#2C2A28] hover:bg-[#7A6547] hover:text-white hover:border-[#7A6547]"
              : "border-white/80 text-white hover:bg-white hover:text-[#2C2A28]"
          }`}
        >
          <Phone size={14} strokeWidth={1.5} />
          (678) 769-9652
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className={`xl:hidden ${scrolled ? "text-[#2C2A28]" : "text-white"}`}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="nav-mobile-menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && <MobileMenu onNavigate={() => setOpen(false)} />}
    </header>
  );
}
